import { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router';
import { useForm, FormProvider } from 'react-hook-form';
import ImportAspenForm from './ImportAspenForm';
import { convertAspenModel } from '@/api/registry';
import { useSpeciesData } from '../hooks/useSpeciesData';
import WizardWrapper from '../WizardWrapper';
import { stringify } from '@/utils/miscUtils';
import MEBalanceInputs from '../MEBalanceInputs';
import TeaInputs from '../TeaInputs';
import AspenBlocks from './AspenBlocks';
import { parseErrorMessage } from '@/utils/errorUtils';

const buildDefaultValues = model => {
  const {
    id,
    permissions,
    data: { inputs, outputs, label, blocks, parameterize },
  } = model;

  const initialParams = [];

  if (blocks?.length && parameterize === true) {
    const firstBlock = blocks[0];
    initialParams.push({
      block: firstBlock.name,
      parameter: '',
      path: '',
      minimum: null,
      baseline: null,
      maximum: null,
      unit: '',
      aspenTooltip: '',
    });
  }

  return {
    id,
    label,
    description: 'Imported from Aspen Simulation',
    permissions,
    model_type: 'aspen',
    inputs: (inputs || []).map(input => ({
      label: input.label,
      name: input.name,
      species: input.species,
      value: input.value,
      unit: stringify(input.unit),
    })),
    outputs: (outputs || []).map(output => ({
      label: output.label,
      name: output.name,
      species: output.species,
      value: output.value,
      unit: stringify(output.unit),
      allocatable: output.allocatable ?? true,
      emissions: output.emissions ?? false,
    })),
    params: initialParams,
    tea_type: 'simple',
    simpleCosts: { spec_type: 'process_only', levelized_costs: [] },
    bottomUpCosts: { reference_product: '' },
  };
};

const ImportAspenPlusModal = () => {
  const [status, setStatus] = useState({
    isSubmitting: false,
    isSuccess: false,
  });
  const [errorMessage, setErrorMessage] = useState(null);
  const [defaultValues, setDefaultValues] = useState(null);
  const [warnings, setWarnings] = useState([]);
  const params = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { typeOptions, typeToUnitsMap, isLoading } = useSpeciesData();
  const model = location.state?.model;
  const modelData = model?.data;

  const methods = useForm({
    defaultValues: {
      files: [],
      parameterize: modelData?.parameterize !== false,
    },
  });

  const steps = [
    { id: 'meBalance', label: 'Part 1: Mass and Energy Balance', Component: MEBalanceInputs },
    ...(modelData?.parameterize === true
      ? [
          {
            id: 'parameters',
            label: 'Part 2: Select Editable Parameters',
            Component: AspenBlocks,
            componentProps: { modelData },
          },
        ]
      : []),
    {
      id: 'tea',
      label: modelData?.parameterize === true ? 'Part 3: TEA' : 'Part 2: TEA',
      Component: TeaInputs,
    },
  ];

  useEffect(() => {
    if (model) {
      setDefaultValues(buildDefaultValues(model));
      setWarnings(modelData?.warnings || []);
    }
  }, [model, modelData?.warnings]);

  const handleClose = () => {
    const { pathwayId, projectId, caseId } = params;
    navigate(`/projects/${projectId}/build/pathways/${pathwayId}/${caseId}`, { replace: true });
  };

  const handleUpload = async () => {
    try {
      setStatus(prev => ({ ...prev, isSubmitting: true }));
      setErrorMessage(null);
      const files = methods.getValues('files');
      const parameterize = methods.getValues('parameterize');

      if (!files?.length) {
        setErrorMessage(['Please select at least one file']);
        setStatus(prev => ({ ...prev, isSubmitting: false }));
        return;
      }

      const formData = new FormData();
      files.forEach(file => {
        formData.append('files', file);
      });

      formData.set('parameterize', parameterize === true ? 'true' : 'false');
      await convertAspenModel(formData);

      const { pathwayId, projectId, caseId } = params;
      // Add timestamp to force fresh navigation and loader revalidation
      navigate(`/projects/${projectId}/build/pathways/${pathwayId}/${caseId}/choose-model?t=${Date.now()}`, {
        replace: true,
      });
    } catch (error) {
      const message = parseErrorMessage(error);
      setErrorMessage(message);
    } finally {
      setStatus(prev => ({ ...prev, isSubmitting: false }));
    }
  };

  const { isSubmitting } = status;
  const { watch } = methods;
  const files = watch('files');
  const isValid = files && files.length > 0;

  // The upload step keeps the plain compact dialog; the parsed model opens in
  // the same full-height builder layout as a Ratio module.
  return (
    <dialog className={`modal ${defaultValues ? 'bg-gray-900/30' : ''}`} open>
      <div
        className={`modal-box ${
          defaultValues
            ? 'w-[95vw] h-[90vh] max-h-[90vh] max-w-[1400px] p-0 flex flex-col border border-gray-200 shadow-2xl'
            : 'w-4/5 max-w-5xl p-8'
        }`}
      >
        <button
          type="button"
          className={`btn btn-sm btn-circle btn-ghost absolute ${defaultValues ? 'right-4 top-4 z-10' : 'right-2 top-2'}`}
          onClick={handleClose}
        >
          ✕
        </button>
        {defaultValues ? (
          <WizardWrapper
            title="Create Model from Aspen Simulation"
            guideUrl="https://docs.sesame.software/user-guides/tutorials/aspen.html"
            defaultValues={defaultValues}
            isLoading={isLoading}
            typeOptions={typeOptions}
            typeToUnitsMap={typeToUnitsMap}
            warnings={warnings}
            steps={steps}
            onSaved={handleClose}
          />
        ) : (
          <>
            <FormProvider {...methods}>
              <form onSubmit={methods.handleSubmit(handleUpload)}>
                <ImportAspenForm isSubmitting={isSubmitting} errorMessage={errorMessage} />
                <div className="flex justify-center mt-6">
                  <button
                    type="button"
                    className={`btn btn-accent btn-sm ${!isValid ? 'btn-disabled' : ''}`}
                    onClick={handleUpload}
                    disabled={!isValid || isSubmitting}
                  >
                    {isSubmitting ? (
                      <>
                        <span className="loading loading-spinner"></span>
                        Please wait...
                      </>
                    ) : (
                      'Upload File'
                    )}
                  </button>
                </div>
              </form>
            </FormProvider>
          </>
        )}
      </div>
    </dialog>
  );
};

export default ImportAspenPlusModal;
